import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { useFarm } from "@/context/FarmContext";

import {
  PageHeader,
  NoFarm,
} from "@/components/Page";

import { Card } from "@/components/ui/card";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Cell,
  Tooltip,
} from "recharts";

import {
  Sprout,
  Gauge,
  CalendarClock,
  Wheat,
  TrendingUp,
  ArrowRight,
  Users,
  Sparkles,
  CircleCheck,
  TriangleAlert,
  CircleAlert,
  Info,
  Activity,
} from "lucide-react";

import { ScoreRing } from "@/modules/NutritionAdvisor";


const LEVEL = {
  danger:
    "border-l-destructive bg-destructive/5",

  warning:
    "border-l-[hsl(43,74%,52%)] bg-[hsl(43,74%,52%)]/5",

  success:
    "border-l-primary bg-primary/5",

  info:
    "border-l-muted-foreground bg-secondary",
};


const GN = {
  danger: {
    cls:
      "border-l-destructive bg-destructive/5",

    chip:
      "bg-destructive/10 text-destructive",
  },

  warning: {
    cls:
      "border-l-[hsl(43,74%,52%)] bg-[hsl(43,74%,52%)]/5",

    chip:
      "bg-[hsl(43,74%,52%)]/15 text-[hsl(38,70%,35%)]",
  },

  success: {
    cls:
      "border-l-primary bg-primary/5",

    chip:
      "bg-primary/10 text-primary",
  },

  info: {
    cls:
      "border-l-muted-foreground bg-secondary",

    chip:
      "bg-secondary text-muted-foreground",
  },
};


function formatNumber(
  value,
  decimals = 0
) {
  if (
    value === null ||
    value === undefined ||
    Number.isNaN(
      Number(value)
    )
  ) {
    return "—";
  }

  return Number(
    value
  ).toLocaleString(
    "en-IE",
    {
      maximumFractionDigits:
        decimals,
    }
  );
}


function DashboardMetric({
  icon: Icon,
  label,
  value,
  unit,
  hint,
}) {
  return (
    <Card
      className="p-4 sm:p-5 border-border"
      data-testid={`metric-${label}`}
    >

      <div className="flex items-center justify-between gap-3 mb-2">

        <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </span>

        <Icon className="h-4 w-4 text-primary shrink-0" />

      </div>


      <div className="flex flex-wrap items-baseline gap-x-1">

        <span className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[hsl(148,38%,16%)] tabular-nums">
          {value}
        </span>

        {unit && (
          <span className="text-xs sm:text-sm font-semibold text-muted-foreground">
            {unit}
          </span>
        )}

      </div>


      {hint && (
        <p className="text-[11px] sm:text-xs text-muted-foreground mt-2 leading-relaxed">
          {hint}
        </p>
      )}

    </Card>
  );
}


function StatusIcon({
  level,
}) {
  if (
    level === "danger"
  ) {
    return (
      <CircleAlert className="h-5 w-5 text-destructive" />
    );
  }

  if (
    level === "warning"
  ) {
    return (
      <TriangleAlert className="h-5 w-5 text-[hsl(43,74%,45%)]" />
    );
  }

  if (
    level === "success"
  ) {
    return (
      <CircleCheck className="h-5 w-5 text-primary" />
    );
  }

  return (
    <Info className="h-5 w-5 text-muted-foreground" />
  );
}


export default function Dashboard() {
  const {
    farmId,
    loading,
  } = useFarm();

  const navigate =
    useNavigate();


  const {
    data,
  } = useQuery({
    queryKey: [
      "dashboard",
      farmId,
    ],

    queryFn: async () =>
      (
        await api.get(
          `/farms/${farmId}/dashboard`
        )
      ).data,

    enabled:
      Boolean(
        farmId
      ),
  });


  const {
    data: advisor,
  } = useQuery({
    queryKey: [
      "advisor-summary",
      farmId,
    ],

    queryFn: async () =>
      (
        await api.get(
          `/farms/${farmId}/advisor`
        )
      ).data,

    enabled:
      Boolean(
        farmId
      ),
  });


  if (
    !loading &&
    !farmId
  ) {
    return (
      <NoFarm />
    );
  }


  if (!data) {
    return (
      <div className="text-muted-foreground">
        Loading dashboard…
      </div>
    );
  }


  const g =
    data.grass;


  // ---------------------------------------------------------
  // Farm setup validation
  // ---------------------------------------------------------

  const farmSetupComplete =
    Number(
      data.farm.cow_numbers
    ) > 0 &&
    Number(
      data.farm.grazing_area
    ) > 0;


  const farmSetupMissing =
    !farmSetupComplete;


  // ---------------------------------------------------------
  // Recommendations
  // ---------------------------------------------------------

  const rawRecommendations =
    data.recommendations ||
    [];


  const recommendations =
    farmSetupMissing
      ? [
          {
            level:
              "warning",

            title:
              "Complete farm setup",

            message:
              "Add cow numbers and grazing area to calculate Days Ahead, herd demand and grass supply recommendations.",

            cta: {
              label:
                "Complete Farm Setup",

              path:
                "/app/farm",
            },
          },
        ]
      : rawRecommendations;


  const dangerCount =
    recommendations.filter(
      (item) =>
        item.level ===
        "danger"
    ).length;


  const warningCount =
    recommendations.filter(
      (item) =>
        item.level ===
        "warning"
    ).length;


  const priorityCount =
    dangerCount +
    warningCount;


  // ---------------------------------------------------------
  // Farm Hand status
  // ---------------------------------------------------------

  let statusLevel =
    "success";

  let statusTitle =
    "Farm on target";

  let statusMessage =
    "No major issues currently need attention.";


  if (
    farmSetupMissing
  ) {
    statusLevel =
      "warning";

    statusTitle =
      "Farm setup incomplete";

    statusMessage =
      "Complete the farm setup before relying on Days Ahead, demand or grass supply recommendations.";

  } else if (
    dangerCount > 0
  ) {
    statusLevel =
      "danger";

    statusTitle =
      `${priorityCount} ${
        priorityCount === 1
          ? "item needs"
          : "items need"
      } attention`;

    statusMessage =
      "Review the priority actions below before making today's management decisions.";

  } else if (
    warningCount > 0
  ) {
    statusLevel =
      "warning";

    statusTitle =
      `${warningCount} ${
        warningCount === 1
          ? "item needs"
          : "items need"
      } attention`;

    statusMessage =
      "Your farm is generally operating normally, but some areas should be reviewed.";

  } else if (
    recommendations.length >
    0
  ) {
    statusTitle =
      "Farm is looking good";

    statusMessage =
      "Current recommendations are mainly optimisation opportunities.";
  }


  // ---------------------------------------------------------
  // Grass wedge data
  // Only show the 12 highest-cover paddocks on Dashboard
  // ---------------------------------------------------------

  const chart = (
    g.paddocks_ranked ||
    []
  )
    .slice(
      0,
      12
    )
    .map(
      (
        paddock
      ) => ({
        name:
          paddock.name ||
          "Unnamed",

        shortName:
          paddock.name &&
          paddock.name.length >
            12
            ? `${paddock.name.slice(
                0,
                11
              )}…`
            : paddock.name ||
              "Unnamed",

        cover:
          Number(
            paddock.current_cover
          ) || 0,
      })
    );


  const nutritionScore =
    advisor?.scorecard
      ?.overall
      ?.score;


  const nutritionGrade =
    advisor?.scorecard
      ?.overall
      ?.grade;


  return (
    <div>

      {/* ----------------------------------------------------- */}
      {/* Farm heading                                          */}
      {/* ----------------------------------------------------- */}

      <PageHeader
        title={
          data.farm.name
        }
        subtitle={`${data.farm.cow_numbers} ${data.farm.herd_type} · ${data.farm.grazing_area} ha grazing platform`}
      />


      {/* ----------------------------------------------------- */}
      {/* Farm Hand Status                                      */}
      {/* ----------------------------------------------------- */}

      <Card className="p-4 sm:p-6 mb-4 sm:mb-6">

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">

          <div className="flex items-start gap-3">

            <div
              className={`rounded-full p-2.5 shrink-0 ${
                statusLevel ===
                "danger"
                  ? "bg-destructive/10"
                  : statusLevel ===
                      "warning"
                    ? "bg-[hsl(43,74%,52%)]/10"
                    : "bg-primary/10"
              }`}
            >

              <StatusIcon
                level={
                  statusLevel
                }
              />

            </div>


            <div>

              <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                Farm Hand Status
              </p>

              <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[hsl(148,38%,16%)] mt-1">
                {statusTitle}
              </h2>

              <p className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
                {statusMessage}
              </p>

            </div>

          </div>


          {advisor && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  "/app/advisor"
                )
              }
              className="inline-flex self-start lg:self-center items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold text-[hsl(148,38%,16%)] hover:bg-secondary transition-colors"
            >

              <Sparkles className="h-4 w-4 text-primary" />

              View Advisor

              <ArrowRight className="h-4 w-4" />

            </button>
          )}

        </div>

      </Card>


      {/* ----------------------------------------------------- */}
      {/* Today's priorities                                    */}
      {/* ----------------------------------------------------- */}

      <Card
        className="p-4 sm:p-6 mb-4 sm:mb-6"
        data-testid="dashboard-recommendations"
      >

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">

          <div>

            <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Today
            </p>

            <h3 className="font-display text-lg sm:text-xl font-bold text-[hsl(148,38%,16%)]">
              Priority actions
            </h3>

          </div>


          {priorityCount >
            0 && (

            <span className="text-xs font-semibold rounded-full bg-secondary px-3 py-1 self-start">

              {priorityCount}{" "}

              {priorityCount ===
              1
                ? "priority"
                : "priorities"}

            </span>

          )}

        </div>


        {recommendations.length >
        0 ? (

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">

            {recommendations.map(
              (
                recommendation,
                index
              ) => (

                <div
                  key={`${recommendation.level}-${recommendation.title}-${index}`}
                  className={`border-l-4 rounded-r-xl p-4 ${LEVEL[recommendation.level]}`}
                  data-testid={`dashboard-rec-${index}`}
                >

                  <div className="flex items-start gap-3">

                    <div className="mt-0.5 shrink-0">

                      <StatusIcon
                        level={
                          recommendation.level
                        }
                      />

                    </div>


                    <div className="min-w-0 flex-1">

                      <p className="font-semibold text-sm sm:text-base text-[hsl(148,38%,16%)]">
                        {recommendation.title}
                      </p>

                      <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                        {recommendation.message}
                      </p>


                      {recommendation.cta && (

                        <button
                          className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold text-primary mt-3 hover:underline"
                          data-testid={`dashboard-rec-cta-${index}`}
                          onClick={() =>
                            navigate(
                              recommendation
                                .cta
                                .path
                            )
                          }
                        >

                          {recommendation
                            .cta
                            .label}

                          <ArrowRight className="h-3.5 w-3.5" />

                        </button>

                      )}

                    </div>

                  </div>

                </div>

              )
            )}

          </div>

        ) : (

          <div className="rounded-xl bg-primary/[0.05] p-4">

            <div className="flex items-center gap-3">

              <CircleCheck className="h-5 w-5 text-primary" />

              <div>

                <p className="font-semibold text-[hsl(148,38%,16%)]">
                  No priority actions
                </p>

                <p className="text-sm text-muted-foreground mt-0.5">
                  Farm Hand has not identified any immediate management issues.
                </p>

              </div>

            </div>

          </div>

        )}

      </Card>


      {/* ----------------------------------------------------- */}
      {/* Grass position                                        */}
      {/* ----------------------------------------------------- */}

      <div className="mb-3">

        <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
          Grass position
        </h3>

        <p className="text-sm text-muted-foreground">
          Key grazing indicators for the selected farm.
        </p>

      </div>


      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">

        <DashboardMetric
          icon={
            Gauge
          }
          label="Avg Cover"
          value={formatNumber(
            g.avg_farm_cover
          )}
          unit="kg DM/ha"
          hint={`Target ${formatNumber(
            g.target_cover
          )}`}
        />


        <DashboardMetric
          icon={
            CalendarClock
          }
          label="Days Ahead"
          value={
            farmSetupMissing
              ? "—"
              : formatNumber(
                  g.days_ahead,
                  1
                )
          }
          unit="days"
          hint={
            farmSetupMissing
              ? "Complete farm setup"
              : "Current grass supply"
          }
        />


        <DashboardMetric
          icon={
            TrendingUp
          }
          label="Growth"
          value={formatNumber(
            g.growth_per_ha,
            1
          )}
          unit="kg/ha/day"
          hint={`Total ${formatNumber(
            g.daily_growth
          )} kg DM/day`}
        />


        <DashboardMetric
          icon={
            Wheat
          }
          label="Demand"
          value={
            farmSetupMissing
              ? "—"
              : formatNumber(
                  g.demand_per_ha,
                  1
                )
          }
          unit="kg/ha/day"
          hint={
            farmSetupMissing
              ? "Complete farm setup"
              : `Total ${formatNumber(
                  g.daily_demand
                )} kg DM/day`
          }
        />

      </div>


      {/* ----------------------------------------------------- */}
      {/* Main analysis                                         */}
      {/* ----------------------------------------------------- */}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 sm:gap-6 mb-4 sm:mb-6">


        {/* Grass wedge */}

        <Card
          className="xl:col-span-8 p-4 sm:p-6"
          data-testid="grass-wedge-card"
        >

          <div className="flex items-start justify-between gap-3 mb-4">

            <div>

              <div className="flex items-center gap-2">

                <Sprout className="h-5 w-5 text-primary" />

                <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
                  Grass Wedge
                </h3>

              </div>

              <p className="text-xs sm:text-sm text-muted-foreground mt-1">

                Highest-cover paddocks · target{" "}

                {formatNumber(
                  g.target_cover
                )}{" "}

                kg DM/ha

              </p>

            </div>


            <button
              type="button"
              onClick={() =>
                navigate(
                  "/app/paddocks"
                )
              }
              className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >

              View Full Wedge

              <ArrowRight className="h-3 w-3" />

            </button>

          </div>


          {chart.length ? (

            <div className="h-[240px] sm:h-[300px] lg:h-[330px]">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={
                    chart
                  }
                  margin={{
                    top: 10,
                    right: 5,
                    left: -20,
                    bottom: 45,
                  }}
                >

                  <XAxis
                    dataKey="shortName"
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                    height={65}
                    tick={{
                      fontSize: 10,
                      fill:
                        "#4A5568",
                    }}
                  />


                  <YAxis
                    width={55}
                    tick={{
                      fontSize: 10,
                      fill:
                        "#4A5568",
                    }}
                  />


                  <Tooltip
                    cursor={{
                      fill:
                        "hsl(138,45%,96%)",
                    }}
                    formatter={(
                      value
                    ) => [
                      `${formatNumber(
                        value
                      )} kg DM/ha`,
                      "Cover",
                    ]}
                    labelFormatter={(
                      label,
                      payload
                    ) =>
                      payload?.[0]
                        ?.payload
                        ?.name ||
                      label
                    }
                  />


                  <ReferenceLine
                    y={
                      g.pre_grazing_max
                    }
                    stroke="hsl(210,60%,50%)"
                    strokeDasharray="4 4"
                  />


                  <ReferenceLine
                    y={
                      g.target_cover
                    }
                    stroke="hsl(43,74%,52%)"
                    strokeDasharray="5 4"
                  />


                  {g.residual >
                    0 && (

                    <ReferenceLine
                      y={
                        g.residual
                      }
                      stroke="hsl(0,61%,46%)"
                      strokeDasharray="3 3"
                    />

                  )}


                  <Bar
                    dataKey="cover"
                    radius={[
                      5,
                      5,
                      0,
                      0,
                    ]}
                  >

                    {chart.map(
                      (
                        item
                      ) => (

                        <Cell
                          key={
                            item.name
                          }
                          fill={
                            item.cover >=
                            g.target_cover
                              ? "hsl(145,47%,35%)"
                              : item.cover <=
                                  g.residual
                                ? "hsl(0,61%,46%)"
                                : "hsl(173,58%,39%)"
                          }
                        />

                      )
                    )}

                  </Bar>

                </BarChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <div className="py-12 text-center">

              <Sprout className="h-8 w-8 text-muted-foreground/40 mx-auto mb-3" />

              <p className="font-medium text-[hsl(148,38%,16%)]">
                No paddock data yet
              </p>

              <p className="text-sm text-muted-foreground mt-1">
                Add paddocks to generate your grass wedge.
              </p>

            </div>

          )}


          <button
            type="button"
            onClick={() =>
              navigate(
                "/app/paddocks"
              )
            }
            className="sm:hidden inline-flex items-center gap-1 text-sm font-semibold text-primary mt-2"
          >

            View Full Grass Wedge

            <ArrowRight className="h-4 w-4" />

          </button>

        </Card>


        {/* Nutrition score */}

        <Card
          className="xl:col-span-4 p-4 sm:p-6"
          data-testid="dashboard-scorecard"
        >

          <div className="flex items-center justify-between gap-3 mb-5">

            <div className="flex items-center gap-2">

              <Sparkles className="h-5 w-5 text-primary" />

              <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
                Nutrition
              </h3>

            </div>


            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              onClick={() =>
                navigate(
                  "/app/advisor"
                )
              }
            >

              Details

              <ArrowRight className="h-3 w-3" />

            </button>

          </div>


          {advisor ? (

            <>
              <div className="flex items-center justify-center gap-4 py-2 sm:py-4">

                <ScoreRing
                  score={
                    nutritionScore
                  }
                  size={88}
                />

                <div>

                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Overall score
                  </p>

                  <p className="font-display text-2xl font-extrabold text-[hsl(148,38%,16%)]">

                    {nutritionScore ??
                      "—"}
                    /100

                  </p>

                  <p className="text-sm font-semibold text-primary">

                    Grade{" "}

                    {nutritionGrade ??
                      "—"}

                  </p>

                </div>

              </div>


              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-2 gap-2 mt-5">

                {Object.values(
                  advisor
                    .scorecard
                    .pillars
                ).map(
                  (
                    pillar
                  ) => (

                    <button
                      key={
                        pillar.key
                      }
                      data-testid={`scorecard-pillar-${pillar.key}`}
                      onClick={() =>
                        navigate(
                          "/app/advisor"
                        )
                      }
                      className="rounded-xl bg-secondary/50 p-3 text-left hover:bg-secondary transition-colors"
                    >

                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold truncate">
                        {pillar.label}
                      </p>

                      <p
                        className="font-display text-xl font-extrabold tabular-nums mt-1"
                        style={{
                          color:
                            pillar.score ==
                            null
                              ? "hsl(0,0%,60%)"
                              : pillar.score >=
                                  85
                                ? "hsl(145,47%,35%)"
                                : pillar.score >=
                                    55
                                  ? "hsl(43,74%,45%)"
                                  : "hsl(0,61%,46%)",
                        }}
                      >

                        {pillar.score ??
                          "—"}

                      </p>

                    </button>

                  )
                )}

              </div>

            </>

          ) : (

            <div className="py-10 text-center text-muted-foreground">

              <Sparkles className="h-8 w-8 mx-auto mb-3 opacity-40" />

              <p className="text-sm">
                Nutrition data is not available yet.
              </p>

            </div>

          )}

        </Card>

      </div>


      {/* ----------------------------------------------------- */}
      {/* Secondary grass detail                                */}
      {/* ----------------------------------------------------- */}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">

        <DashboardMetric
          icon={
            Sprout
          }
          label="Grass Available"
          value={formatNumber(
            g.total_grass
          )}
          unit="kg DM"
          hint={
            Number(
              g.residual
            ) > 0
              ? `Above ${formatNumber(
                  g.residual
                )} kg DM/ha residual`
              : "Set grazing residual in Farm Management"
          }
        />


        <DashboardMetric
          icon={
            Activity
          }
          label="Growth Balance"
          value={
            farmSetupMissing
              ? "—"
              : formatNumber(
                  g.growth_balance,
                  1
                )
          }
          unit="kg/ha/day"
          hint={
            farmSetupMissing
              ? "Complete farm setup"
              : g.growth_balance >=
                  0
                ? "Growth exceeds demand"
                : "Demand exceeds growth"
          }
        />


        <Card className="p-4 sm:p-5 col-span-2 lg:col-span-1">

          <div className="flex items-center justify-between gap-3 mb-2">

            <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Grass Position
            </span>

            <Gauge className="h-4 w-4 text-primary" />

          </div>


          {farmSetupMissing ? (

            <>
              <p className="font-display text-lg sm:text-xl font-bold text-[hsl(148,38%,16%)]">
                Setup required
              </p>

              <p className="text-[11px] sm:text-xs text-muted-foreground mt-2 leading-relaxed">
                Complete cow numbers and grazing area before interpreting grass balance.
              </p>
            </>

          ) : (

            <>
              <p className="font-display text-lg sm:text-xl font-bold text-[hsl(148,38%,16%)]">

                {g.growth_balance >=
                0
                  ? "Building"
                  : "Declining"}

              </p>


              <p className="text-[11px] sm:text-xs text-muted-foreground mt-2 leading-relaxed">

                Current growth is{" "}

                {g.growth_balance >=
                0
                  ? "above"
                  : "below"}{" "}

                estimated herd demand.

              </p>
            </>

          )}

        </Card>

      </div>


      {/* ----------------------------------------------------- */}
      {/* Group nutrition                                       */}
      {/* ----------------------------------------------------- */}

      {data.group_nutrition &&
        data.group_nutrition
          .length > 0 && (

        <Card
          className="p-4 sm:p-6"
          data-testid="dashboard-group-nutrition"
        >

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">

            <div className="flex items-center gap-2">

              <Users className="h-5 w-5 text-primary" />

              <div>

                <h3 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
                  Herd Nutrition
                </h3>

                <p className="text-xs sm:text-sm text-muted-foreground">
                  Nutrition status by animal group.
                </p>

              </div>

            </div>


            <button
              type="button"
              onClick={() =>
                navigate(
                  "/app/animals"
                )
              }
              className="inline-flex self-start items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >

              Animal Groups

              <ArrowRight className="h-3 w-3" />

            </button>

          </div>


          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">

            {data.group_nutrition.map(
              (
                group,
                index
              ) => {
                const style =
                  GN[
                    group.level
                  ] ||
                  GN.info;

                return (

                  <button
                    key={
                      group.group_id
                    }
                    data-testid={`dashboard-group-${index}`}
                    onClick={() =>
                      navigate(
                        group.cta
                          ?.path ||
                          "/app/animals"
                      )
                    }
                    className={`text-left rounded-xl border-l-4 p-4 transition-colors hover:bg-secondary/40 ${style.cls}`}
                  >

                    <div className="flex items-start justify-between gap-3">

                      <p className="font-semibold text-sm text-[hsl(148,38%,16%)]">
                        {group.group_name}
                      </p>


                      <span className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full whitespace-nowrap ${style.chip}`}>
                        {group.title}
                      </span>

                    </div>


                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      {group.message}
                    </p>


                    <p className="text-[11px] text-muted-foreground mt-3">

                      {group.ration_name
                        ? `Ration: ${group.ration_name}`
                        : "No ration assigned"}

                    </p>

                  </button>

                );
              }
            )}

          </div>

        </Card>

      )}

    </div>
  );
}